import _extends from '@babel/runtime/helpers/extends';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import EnterpriseIcon, { PlusCircleIcon } from '@enterprise-ui/icons';
import classnames from 'classnames';
import React__default, { useState, useRef, useCallback, useEffect } from 'react';
import useCanvasId from '../../utils/useCanvasId.js';

var _excluded = ["className", "id", "disabled", "instructionText", "fullwidth", "dropText", "data-testid", "clickOnly", "onUpdate", "required", "size", "error"];
var InputDropArea = function InputDropArea(_ref) {
  var className = _ref.className,
    id = _ref.id,
    disabled = _ref.disabled,
    _ref$instructionText = _ref.instructionText,
    instructionText = _ref$instructionText === void 0 ? 'Drop file or click here to upload' : _ref$instructionText,
    fullwidth = _ref.fullwidth,
    _ref$dropText = _ref.dropText,
    dropText = _ref$dropText === void 0 ? 'Add file' : _ref$dropText,
    dataTestId = _ref['data-testid'],
    clickOnly = _ref.clickOnly,
    onUpdate = _ref.onUpdate,
    required = _ref.required,
    _ref$size = _ref.size,
    size = _ref$size === void 0 ? 'normal' : _ref$size;
    _ref.error;
    var otherProps = _objectWithoutProperties(_ref, _excluded);
  var uuid = useCanvasId(id, 'DropArea');
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    dragEnter = _useState2[0],
    setDragEnter = _useState2[1];
  var inputElement = useRef(null);
  var dropTargetRef = useRef(null);
  var callUpdate = useCallback(function (nextValue) {
    typeof onUpdate === 'function' && onUpdate(nextValue);
  }, [onUpdate]);
  var handleDrop = useCallback(function (e) {
    e.preventDefault();
    if (!disabled) {
      setDragEnter(false);
      callUpdate(e);
    }
  }, [callUpdate, disabled]);
  var handleDragOver = function handleDragOver(e) {
    e.preventDefault();
  };
  var handleDragEnter = function handleDragEnter() {
    setDragEnter(true);
  };
  var handleDragLeave = function handleDragLeave() {
    setDragEnter(false);
  };
  useEffect(function () {
    if (!clickOnly && dropTargetRef && dropTargetRef.current) {
      var dropTarget = dropTargetRef.current;
      dropTarget.addEventListener('dragenter', handleDragEnter, false);
      dropTarget.addEventListener('dragleave', handleDragLeave, false);
      dropTarget.addEventListener('dragover', handleDragOver, false);
      dropTarget.addEventListener('drop', handleDrop, false);

      // returned function will be called on component unmount
      return function () {
        dropTarget.removeEventListener('dragenter', handleDragEnter, false);
        dropTarget.removeEventListener('dragleave', handleDragLeave, false);
        dropTarget.removeEventListener('dragover', handleDragOver, false);
        dropTarget.removeEventListener('drop', handleDrop, false);
      };
    }
  }, [clickOnly, handleDrop]);
  var handleOpenFileSelector = function handleOpenFileSelector(e) {
    e.preventDefault();
    inputElement.current.click();
  };
  var dropAreaClasses = classnames('C-DropArea', _defineProperty(_defineProperty({
    '--clickOnly': clickOnly,
    '--fullwidth': fullwidth
  }, "--size-".concat(size), size), "isDisabled", disabled), className);
  var hoverClasses = classnames('C-DropArea__Hover', {
    isDisabled: disabled,
    isHover: dragEnter
  }, className);
  if (otherProps['dense'] || otherProps['expanded']) {
    console.error('Hey Canvas User! The DropArea component no longer supports the "expanded" and "dense" properties. Please use "size".');
  }
  return /*#__PURE__*/React__default.createElement("label", {
    className: dropAreaClasses,
    htmlFor: id,
    "aria-label": "Click to upload a file. ".concat(required ? 'Required field.' : ' '),
    "data-testid": dataTestId && "".concat(dataTestId, "_container")
  }, /*#__PURE__*/React__default.createElement("div", {
    className: "C-DropArea__DropText"
  }, /*#__PURE__*/React__default.createElement(EnterpriseIcon, {
    icon: PlusCircleIcon
  }), dropText ? /*#__PURE__*/React__default.createElement("p", null, dropText) : null), instructionText ? /*#__PURE__*/React__default.createElement("div", {
    xs: 12,
    className: "C-DropArea__InstructionText"
  }, /*#__PURE__*/React__default.createElement("p", null, instructionText)) : null, /*#__PURE__*/React__default.createElement("input", _extends({}, otherProps, {
    onChange: callUpdate,
    disabled: disabled,
    "data-testid": dataTestId,
    ref: inputElement,
    className: "C-DropArea__Input",
    type: "file",
    id: id ? id : uuid,
    required: required,
    "aria-hidden": "true",
    tabIndex: "-1"
  })), /*#__PURE__*/React__default.createElement("button", {
    type: "button",
    disabled: disabled,
    onClick: handleOpenFileSelector,
    className: hoverClasses,
    ref: dropTargetRef
  }));
};
InputDropArea.displayName = 'Input.DropArea';

export { InputDropArea, InputDropArea as default };
